import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { RetailersService, InventoriesService, InventoryRequest, BrandsService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { FilterOperator } from '@src/app/shared/enums/FilterOperator.enum';

@Component({
    selector: 'app-add-inventory',
    templateUrl: './add-inventory.component.html',
    styleUrl: './add-inventory.component.scss',
    host: {
        class: 'app-add-inventory'
    },
    standalone: false
})
export class AddInventoryComponent implements OnInit {

  combosData: { [key: string]: ComboData } = {
    retailers: new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter))),
    brands: new ComboData(async (filter) => {
      const activeBrandsFilter = {
        propertyName: 'isActive',
        operator: FilterOperator.EQUALS,
        values: ['true']
      };

      const brandsFilter = {
        ...filter,
        additionalFilters: [
          ...(filter.additionalFilters ?? []),
          activeBrandsFilter
        ]
      };

      const retailerId = this.inventoryForm?.controls['retailerId']?.value?.[0];

      if (retailerId) {
        brandsFilter.additionalFilters.push({
          propertyName: 'retailers.id',
          operator: FilterOperator.IN,
          values: [retailerId.toString()]
        });
      }

      return await firstValueFrom(
        this.brandsService.apiBrandsFilterComboPost(brandsFilter)
      );
    })
  };

  companyBrandsTotalRecords = 0;
  hasOnlyOneActiveBrand = false;

  public inventoryForm: FormGroup = this.fb.group({
    reference:  ['', [Validators.required, Validators.maxLength(50)]],
    retailerId: [[], [Validators.required]],
    comment:  ['', [Validators.maxLength(255)]],
    brandId: [{ value: [], disabled: true }, [Validators.required]]
  });

  constructor(
    private router: Router,
    private retailersService: RetailersService,
    private inventoriesService: InventoriesService,
    private brandsService: BrandsService,
    private fb: FormBuilder
  ) { }

  async ngOnInit() {
    await this.initCombosData();

    this.inventoryForm.controls['retailerId'].valueChanges.subscribe(async (retailerIds) => {
      const brandControl = this.inventoryForm.controls['brandId'];

      brandControl.setValue([]);
      brandControl.disable();
      this.hasOnlyOneActiveBrand = false;

      const retailerId = retailerIds?.[0];

      if (!retailerId) {
        return;
      }

      await this.combosData['brands'].getData(null, 1);

      brandControl.enable();

      this.hasOnlyOneActiveBrand = this.combosData['brands'].totalRecords === 1;
      this.autoSelectBrandWhenOnlyOneIsAvailable();
    });
  }

  private async initCombosData(): Promise<void> {
    await Promise.all([
      this.combosData['retailers'].getData(null, 1),
      this.getCompanyBrandsTotalRecords()
    ]);
  }

  private async getCompanyBrandsTotalRecords(): Promise<void> {
    const payload: FilterRequestForCombo = {
      pageNumber: 1,
      pageSize: 10,
      additionalFilters: [
        {
          propertyName: 'isActive',
          operator: FilterOperator.EQUALS,
          values: ['true']
        }
      ]
    };

    const brandsPage = await firstValueFrom(
      this.brandsService.apiBrandsFilterComboPost(payload)
    );

    this.companyBrandsTotalRecords = brandsPage.totalCount ?? 0;
  }

  async onLazyLoadComboData(comboType: 'retailers' | 'brands', event: any) {
    const retailerId = this.inventoryForm.controls['retailerId'].value?.[0];

    if (comboType === 'brands' && !retailerId) {
      return;
    }

    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
  }

  getComboTotalPages(comboType: 'retailers' | 'brands' ): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  async submitForm() {
    if (this.inventoryForm.valid) {
      const payload: InventoryRequest = {
        name: this.inventoryForm.controls['reference'].value,
        retailerId: Number(this.inventoryForm.controls['retailerId'].value?.[0]),
        comment: this.inventoryForm.controls['comment'].value,
        brandId: this.inventoryForm.controls['brandId'].value?.[0] ?? null
      };

      const inventoryId = await firstValueFrom(this.inventoriesService.apiInventoriesCreatePost(payload));
      this.router.navigate(['/inventories', inventoryId], { replaceUrl: true });
    }
  }

  private autoSelectBrandWhenOnlyOneIsAvailable(): void {
    if (!this.hasOnlyOneActiveBrand) {
      return;
    }

    const brandId = this.combosData['brands'].list?.[0]?.id;

    if (brandId == null) {
      return;
    }

    this.inventoryForm.get('brandId')?.setValue([brandId]);
  }
}
